<template>
    <div class="mobile-blocker">
        <div class="blocker-content">
            <img
                src="/images/ForGreyLogo.png"
                alt="Brittany Palazzo Premier Management Logo"
                class="blocker-logo"
            />

            <span class="material-symbols-outlined blocker-icon">smartphone</span>

            <h1 class="blocker-headline">Mobile Access Restricted</h1>

            <p class="blocker-description">
                This application is optimized for desktop and tablet devices.
                For the best experience, please access the portal from a device with a screen width of 768px or larger.
            </p>

            <div class="blocker-actions">
                <a
                    :href="supportMailto"
                    class="btn-primary"
                    aria-label="Request mobile support"
                >
                    Request Mobile Support
                </a>

                <button
                    type="button"
                    class="btn-secondary"
                    @click="showModal = true"
                    aria-label="Learn more about mobile access restrictions"
                >
                    Learn More
                </button>
            </div>

            <footer class="blocker-footer">
                <p>&copy; {{ currentYear }} Brittany Palazzo. All rights reserved.</p>
            </footer>
        </div>

        <Transition name="fade">
            <div v-if="showModal" class="modal-overlay" @click="closeModal" role="dialog" aria-modal="true" aria-labelledby="modal-title">
                <div class="modal-content" @click.stop role="document">
                    <button
                        type="button"
                        class="modal-close"
                        @click="closeModal"
                        aria-label="Close modal"
                    >
                        <span class="material-symbols-outlined">close</span>
                    </button>

                    <h2 id="modal-title" class="modal-title">Why is Mobile Access Restricted?</h2>

                    <div class="modal-body">
                        <div class="faq-item">
                            <h3 class="faq-question">Is this a permanent restriction?</h3>
                            <p class="faq-answer">
                                Not necessarily. This restriction is in place to ensure you have the best experience
                                when managing client billing, events, and other portal features. We are continuously
                                evaluating mobile optimization options.
                            </p>
                        </div>

                        <div class="faq-item">
                            <h3 class="faq-question">Can I still access my account?</h3>
                            <p class="faq-answer">
                                Yes, absolutely. You can access your account from any tablet (iPad, Android tablet) or
                                desktop computer with a screen width of 768px or larger.
                            </p>
                        </div>

                        <div class="faq-item">
                            <h3 class="faq-question">What features require desktop/tablet access?</h3>
                            <p class="faq-answer">
                                The full portal functionality including client management, event scheduling, billing
                                operations, and report generation is optimized for larger screens.
                            </p>
                        </div>

                        <div class="faq-item">
                            <h3 class="faq-question">How do I request mobile support?</h3>
                            <p class="faq-answer">
                                Click the "Request Mobile Support" button above to send an email to our support team.
                                Please include your use case and we'll get back to you within 24-48 hours.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

const showModal = ref(false)
const currentYear = new Date().getFullYear()

const supportMailto = computed(() => {
    const subject = encodeURIComponent('Mobile Access Request')
    const body = encodeURIComponent(
        `Hello,\n\nI am requesting mobile access to the Brittany Palazzo Payment Portal.\n\nPlease assist.\n\nThank you.`
    )
    return `mailto:noreply@brittanypalazzo.com?subject=${subject}&body=${body}`
})

const closeModal = () => {
    showModal.value = false
}

const handleKeydown = (event) => {
    if (event.key === 'Escape' && showModal.value) {
        closeModal()
    }
}

onMounted(() => {
    document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
.mobile-blocker {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #fff8f3;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

.blocker-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 2rem;
    max-width: 480px;
    width: 100%;
}

.blocker-logo {
    max-height: 120px;
    width: auto;
    object-fit: contain;
    margin-bottom: 2rem;
}

.blocker-icon {
    font-size: 64px;
    color: #775928;
    margin-bottom: 1.5rem;
}

.blocker-headline {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 600;
    font-size: 32px;
    color: #1e1b17;
    margin: 0 0 1rem 0;
}

.blocker-description {
    font-family: 'Manrope', sans-serif;
    font-size: 1rem;
    line-height: 1.6;
    color: #4e453a;
    margin: 0 0 2rem 0;
    max-width: 100%;
}

.blocker-actions {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    max-width: 300px;
}

.btn-primary {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(to bottom, #775928, #b08d57);
    color: #ffffff;
    font-family: 'Manrope', sans-serif;
    font-weight: 600;
    font-size: 1rem;
    padding: 1rem 2rem;
    border-radius: 9999px;
    text-decoration: none;
    border: none;
    cursor: pointer;
    min-height: 48px;
    min-width: 48px;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.btn-primary:hover {
    transform: scale(1.02);
    box-shadow: 0px 12px 32px rgba(52, 48, 43, 0.08);
}

.btn-primary:focus-visible {
    outline: 2px solid #775928;
    outline-offset: 2px;
}

.btn-secondary {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #f5ece5;
    color: #775928;
    font-family: 'Manrope', sans-serif;
    font-weight: 600;
    font-size: 1rem;
    padding: 1rem 2rem;
    border-radius: 9999px;
    text-decoration: none;
    border: none;
    cursor: pointer;
    min-height: 48px;
    min-width: 48px;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.btn-secondary:hover {
    transform: scale(1.02);
    box-shadow: 0px 12px 32px rgba(52, 48, 43, 0.06);
}

.btn-secondary:focus-visible {
    outline: 2px solid #775928;
    outline-offset: 2px;
}

.blocker-footer {
    margin-top: 3rem;
}

.blocker-footer p {
    font-family: 'Manrope', sans-serif;
    font-size: 0.875rem;
    color: #807569;
    margin: 0;
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(30, 27, 23, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    padding: 1rem;
}

.modal-content {
    background: #ffffff;
    border-radius: 0.75rem;
    padding: 2rem;
    max-width: 480px;
    width: 100%;
    max-height: 80vh;
    overflow-y: auto;
    position: relative;
}

.modal-close {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0.5rem;
    color: #807569;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 48px;
    min-width: 48px;
    border-radius: 9999px;
    transition: background-color 0.2s ease;
}

.modal-close:hover {
    background: #f5ece5;
}

.modal-close:focus-visible {
    outline: 2px solid #775928;
    outline-offset: 2px;
}

.modal-close .material-symbols-outlined {
    font-size: 24px;
}

.modal-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 600;
    font-size: 1.5rem;
    color: #1e1b17;
    margin: 0 0 1.5rem 0;
    padding-right: 2rem;
}

.modal-body {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.faq-item {
    border-bottom: 1px solid #d1c5b6;
    padding-bottom: 1.5rem;
}

.faq-item:last-child {
    border-bottom: none;
    padding-bottom: 0;
}

.faq-question {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 600;
    font-size: 1rem;
    color: #1e1b17;
    margin: 0 0 0.5rem 0;
}

.faq-answer {
    font-family: 'Manrope', sans-serif;
    font-size: 0.875rem;
    line-height: 1.6;
    color: #4e453a;
    margin: 0;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

@media (max-width: 767px) {
    .mobile-blocker {
        display: flex;
    }
}
</style>